import paper from 'paper';

import { Injectable, NgZone } from '@angular/core';
import { EventName, Events } from '../../utils/events';
import { BaseProvider } from '../base';
import { ClassProvider, Class } from '../class';
import { TaggableAsset } from '../asset';
import { TaggableCanvasAsset } from '../../components/workspace/objects/taggableCanvasAsset';
import { Polygon } from '../../components/workspace/objects/polygon';
import { Collision } from '../../components/workspace/objects/collision';
import { NeuralaProject } from '../../components/workspace/objects/project';
import { SettingsProvider } from '../settings';
import { ModalProvider } from '../popup/modal';
import { RegionOfInterest } from '../../components/workspace/objects/region-of-interest';
import { GridLayer, GridState } from '../../components/workspace/objects/grid';
import { WorkspaceType } from '../workspace_enums';
import { Subscription } from 'rxjs';
import { MuteableModalProvider } from '../popup/muteable-modal';
import { ClipperExPolygon, ClipperLibInterface, ClipperPoint } from './clipper';
import { TranslateService } from '@ngx-translate/core';

declare var ClipperLib: ClipperLibInterface;

interface CanvasSettings {
    labelsEnabled: boolean;
    predictionsEnabled: boolean;
    noClassColor: string | null;
}

export interface ExportedPolygon {
    clientIdentifier: number;
    polygonIdentifier?: string;
    data: number[][][] | null;
    classIdentifier: string;
    className: string;
    prediction?: object;
    neuralaClass?: Class;
    predictionFlag?: boolean;
}

export enum StackAction {
    CREATE = 'Create',
    UPDATE = 'Update',
    DELETE = 'Delete',
    SET = 'Set'
}

export interface StackItem {
    action: StackAction;
    saveable: boolean;
    polygons: ExportedPolygon[];
}

export interface AssetBoundingClientRect {
    x: number;
    y: number;
    top: number;
    right: number;
    bottom: number;
    left: number;
    width: number;
    height: number;
}

export interface ROICoordinates {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}
export interface SnapPayload {
    snap: boolean;
    point: paper.Point;
}

export interface BoxResizeSubscription {
    index: number;
    subscription: Subscription;
}

export interface AssetRoom {
    top: boolean;
    right: boolean;
    bottom: boolean;
    left: boolean;
}

export const SVG_TEXT_HEIGHT = 25;
export const SVG_TEXT_COORDINATES_WIDTH = 110;
export const SVG_TEXT_DIMENSIONS_WIDTH = 130;

interface NumberMap {
    [key: number]: number;
}

interface NumberToStringMap {
    [key: number]: string;
}

interface ChangeZoomEvent {
    layerX: number;
    layerY: number;
    deltaX: number;
    deltaY: number;
    stopPropagation: () => void;
}

interface ScalingData {
    x: number;
    y: number;
}

interface ScalableImage {
    getScaling(): ScalingData;
}

interface CollisionsMap {
    [key: string]: boolean;
}

interface ExtendedView {
    _needsUpdate: boolean;
    _context: CanvasRenderingContext2D;
}

interface ExtendedProject {
    _needsUpdate: boolean;
    draw(context: CanvasRenderingContext2D, matrix: paper.Matrix, pixelRatio: number): void;
}

interface ExtendedCanvasFilters {
    filter: string;
}

@Injectable()
export class CanvasProvider extends BaseProvider {
    public ready = false;
    public locked = false;

    public allowUnsavedChangesEvent = true;

    public isDragging = true;

    private undoRedoStack: StackItem[][] = [];
    private _hasUnsavedChanges = false;
    private currentStep = 0;
    private savedOnStep = 0;
    private polygonClipboard: Polygon[] = [];

    private scope: paper.PaperScope;
    private project: NeuralaProject | null = null;
    private canvas: HTMLCanvasElement;
    private initialCenter: paper.Point | null = null;
    private initialZoom = 1;

    private activePolygons: Polygon[] = [];
    private activePolygonsIndex: NumberMap = {};

    private activeSegment: paper.Segment | null = null;
    private activeImage: TaggableAsset | null = null;

    private asset: TaggableCanvasAsset;
    private assetLayer: paper.Layer;
    private polygonsLayer: paper.Layer;

    public hasOverlaps = false;

    public labelsEnabled = true;
    public predictionsEnabled = true;

    private wheelHandler = this.handleWheelEvent.bind(this);

    private scrollWheelEnabled = true;
    private shiftPressed = false;

    private tempRectangle: paper.Rectangle = new paper.Rectangle(0, 0, 10, 10);

    public noClassColor = '#FFFFFF';

    public defaultClass: Class | null = null;

    private roi: RegionOfInterest | null = null;
    private originalROIPoints: ROICoordinates | null = null;
    private grid: GridLayer | null = null;

    private FLOATING_POINT_ERROR_THRESHOLD = 0.000001;

    private boundingBoxResizeSubscriptions: BoxResizeSubscription[] = [];
    private initialBoxState: StackItem | null = null; // Needed for a workaround when resizing bounding boxes

    constructor(
        private classProvider: ClassProvider,
        private modalProvider: ModalProvider,
        private events: Events,
        private zone: NgZone,
        private settingsProvider: SettingsProvider,
        private muteableModalProvider: MuteableModalProvider,
        private translate: TranslateService
    ) {
        super();
    }

    public isReady(): boolean {
        return this.ready;
    }

    public set polygonMovingInProgress(state: boolean) {
        if (this.project) {
            this.project.polygonMovingInProgress = state;
        }
    }

    public get polygonMovingInProgress(): boolean {
        if (this.project) {
            return this.project.polygonMovingInProgress;
        } else {
            return false;
        }
    }

    public set polygonResizingInProgress(state: boolean) {
        if (this.project) {
            this.project.polygonResizingInProgress = state;
        }
    }

    public get polygonResizingInProgress(): boolean {
        if (this.project) {
            return this.project.polygonResizingInProgress;
        } else {
            return false;
        }
    }

    public get canRedo() {
        return this.currentStep < this.undoRedoStack.length;
    }

    public get canUndo() {
        return this.currentStep > 0;
    }

    public get hasUnsavedChanges() {
        return this._hasUnsavedChanges;
    }

    public set hasUnsavedChanges(newValue: boolean) {
        this._hasUnsavedChanges = newValue;
    }

    public get hasUnsavedChangesInROI() {
        return this.roi && this.roi.hasChanges();
    }

    private updateUnsavedChangesFlag() {
        if (this.currentStep > this.savedOnStep) {
            for (let i = this.savedOnStep; i < this.currentStep; i++) {
                const stackItems = this.undoRedoStack[i];
                if (stackItems && stackItems.some(stackItem => stackItem && stackItem.saveable)) {
                    this._hasUnsavedChanges = true;
                    return;
                }
            }
        } else if (this.currentStep < this.savedOnStep) {
            for (let i = this.currentStep - 1; i < this.savedOnStep; i++) {
                const stackItems = this.undoRedoStack[i];
                if (stackItems && stackItems.some(stackItem => stackItem && stackItem.saveable)) {
                    this._hasUnsavedChanges = true;
                    return;
                }
            }
        }
        this._hasUnsavedChanges = false;
    }

    public hasROI(): boolean {
        return this.roi !== null && this.roi.isDefined();
    }

    public canDeleteROI(): boolean {
        return this.roi !== null && this.roi.canDelete();
    }

    public getROI(): RegionOfInterest | null {
        return this.roi;
    }

    public getGrid(): GridLayer | null {
        return this.grid;
    }

    public deleteROI(): void {
        if (this.roi) {
            this.roi.drawArea();
        }
    }

    public toggleROIVisibility(visibility: boolean): void {
        if (this.roi) {
            this.roi.visible = visibility;
        }
    }

    public exportROI() {
        if (!this.roi || !this.roi.isDefined()) {
            return null;
        }
        return [
            this.normalizePointToArray(this.roi.getBounds().topLeft),
            this.normalizePointToArray(this.roi.getBounds().bottomLeft),
            this.normalizePointToArray(this.roi.getBounds().topRight),
            this.normalizePointToArray(this.roi.getBounds().bottomRight)
        ];
    }

    public exportROICooordinates() {
        if (!this.roi || !this.roi.isDefined()) {
            return null;
        }
        return {
            x1: this.normalizePoint(this.roi.getBounds().topLeft).x,
            y1: this.normalizePoint(this.roi.getBounds().topLeft).y,
            x2: this.normalizePoint(this.roi.getBounds().bottomRight).x,
            y2: this.normalizePoint(this.roi.getBounds().bottomRight).y
        };
    }

    public isROIBiggerThan(size: number, roi: number[][]): boolean {
        const asset = this.getAsset();
        if (!asset) {
            return false;
        }

        const xCoords = roi.map(point => point[0]);
        const yCoords = roi.map(point => point[1]);
        const minX = Math.min(...xCoords);
        const maxX = Math.max(...xCoords);
        const minY = Math.min(...yCoords);
        const maxY = Math.max(...yCoords);

        const width = (maxX - minX) * asset.width;
        const height = (maxY - minY) * asset.height;
        return width >= size && height >= size;
    }

    public convertPolygonToROI(polygon: Polygon): number[][] {
        const [x1, x2, y1, y2] = polygon.getPoints(this.normalizePoint.bind(this)).reduce(
            (acc, currentValue) => {
                return currentValue.reduce((innerAcc, currentValue) => {
                    const [x1, x2, y1, y2] = innerAcc;
                    return [
                        x1 > currentValue[0] ? currentValue[0] : x1,
                        x2 < currentValue[0] ? currentValue[0] : x2,
                        y1 > currentValue[1] ? currentValue[1] : y1,
                        y2 < currentValue[1] ? currentValue[1] : y2
                    ];
                }, acc);
            },
            [Infinity, -Infinity, Infinity, -Infinity]
        );

        return [
            [x1, y1],
            [x1, y2],
            [x2, y1],
            [x2, y2]
        ];
    }

    public convertToPoints(roi: any): number[][] {
        return [
            [roi.x1, roi.y1],
            [roi.x1, roi.y2],
            [roi.x2, roi.y1],
            [roi.x2, roi.y2]
        ];
    }

    public markROIAsSaved() {
        if (this.roi) {
            this.roi.markAsSaved();
        }
    }

    public initializeROI(roiCoordinates: ROICoordinates | null = null): void {
        if (this.roi) {
            return;
        }

        let originalROIPoint: paper.Point | null = null;
        let originalROISize: paper.Size | null = null;
        this.originalROIPoints = roiCoordinates;

        if (roiCoordinates) {
            const points = this.convertToPoints(roiCoordinates);
            const denormalizedPoints = this.denormalize(points);

            // This may be undefined if the the asset/view/canvas is missing
            // In that case we just abort
            if (denormalizedPoints === undefined) {
                return;
            }

            const roiRectangle = new paper.Path(denormalizedPoints);
            originalROIPoint = roiRectangle.bounds.point.clone();
            originalROISize = roiRectangle.bounds.size.clone();
        } else {
            originalROIPoint = new paper.Point(0, 0);
            originalROISize = new paper.Size(0, 0);
        }

        this.roi = new RegionOfInterest(originalROIPoint, originalROISize);
    }

    public drawROI(points: ROICoordinates | null = null): void {
        if (!this.roi) {
            this.initializeROI(this.originalROIPoints);
        }

        const project = this.getProject();
        if (!project) {
            return;
        }

        if (this.roi) {
            if (points) {
                const roiPoints = this.convertToPoints(points);
                const denormalizedPoints = this.denormalize(roiPoints);

                // This might happen if the asset/view/canvas is missing
                // In that case, let's just abort
                if (!denormalizedPoints) {
                    return;
                }

                const roiRectangle = new paper.Path(denormalizedPoints);
                roiRectangle.closed = true;
                this.roi.visible = true;
                this.roi.drawArea(roiRectangle.bounds.point, roiRectangle.bounds.size);
            } else {
                this.roi.drawArea();
                this.roi.visible = false;
            }

            this.roi.addTo(project);
            this.roi.bringToFront();
        }
    }

    public resetInfo(): void {
        this.reset();
    }

    public setLockState(state: boolean = false) {
        this.locked = state;
    }

    public setCursor(cursor: string = 'default') {
        const canvas = this.getCanvas();
        if (canvas) {
            canvas.style.cursor = cursor;
        }
    }

    private emitSaveableChange(): void {
        if (this.allowUnsavedChangesEvent) {
            this.events.publish(EventName.CanvasSaveableChange);
        }
    }

    public markAllItemsAsSaved() {
        this.savedOnStep = this.currentStep;
        this.updateUnsavedChangesFlag();
    }

    /***********************
     *  Undo/Redo Methods  *
     **********************/

    public clearUndoStack(): void {
        this.undoRedoStack = [];
        this.currentStep = 0;
        this.savedOnStep = 0;
        this.updateUnsavedChangesFlag();
    }

    public clearRedoStack(): void {
        this.undoRedoStack.splice(this.currentStep);
        if (this.savedOnStep > this.currentStep) {
            this.savedOnStep = 0;
        }
        this.currentStep = this.undoRedoStack.length;
        this.updateUnsavedChangesFlag();
    }

    public revertAllChanges(): void {
        while (this.currentStep - this.savedOnStep != 0) {
            if (this.currentStep > this.savedOnStep) {
                this.undoSingleStackAction();
            } else {
                this.redoSingleStackAction();
            }
        }

        this.detectCollisions();
        this.updateUnsavedChangesFlag();
    }

    public undo(): void {
        if (this.currentStep <= 0) {
            return;
        }
        this.undoSingleStackAction();

        this.detectCollisions();
        this.updateUnsavedChangesFlag();
    }

    private undoSingleStackAction() {
        this.currentStep--;
        const stackItems = this.undoRedoStack[this.currentStep];
        if (!stackItems || stackItems.length == 0) {
            return;
        }
        const newStates = this.performStackActions(stackItems);
        this.undoRedoStack[this.currentStep] = newStates;
    }

    public redo(): void {
        this.redoSingleStackAction();
        this.detectCollisions();
        this.updateUnsavedChangesFlag();
    }

    private redoSingleStackAction(): void {
        if (this.currentStep >= this.undoRedoStack.length) {
            return;
        }
        const stackItems = this.undoRedoStack[this.currentStep];
        this.currentStep++;
        if (!stackItems || stackItems.length == 0) {
            this.updateUnsavedChangesFlag();
            return;
        }

        const newStates = this.performStackActions(stackItems);
        this.undoRedoStack[this.currentStep - 1] = newStates;
    }

    private performStackActions(stackItems: StackItem[]): StackItem[] {
        const result: StackItem[] = [];

        stackItems.forEach(stackItem => {
            const actionResult = this.performStackAction(stackItem);
            if (actionResult) {
                result.push(actionResult);
            }
        });

        return result;
    }

    /**
     * Creates a stack item for undo/redo stack
     *
     * @param {StackAction} action Performed action: CREATE, UPDATE, DELETE
     * @param {Polygon[]} polygons List of polygons affected by the change
     * @param {boolean} willClosePolygon Set to true in case change you apply will close the polygons
     */
    public createStackItem(action: StackAction, polygons: Polygon[], willClosePolygon: boolean = false): StackItem {
        return this.generateNewStackItem(action, polygons, willClosePolygon);
    }

    public addToUndoStack(stackItems: StackItem[]): void {
        const stackItemsToAdd = stackItems.filter(stackItem => stackItem.polygons && stackItem.polygons.length > 0);
        if (stackItemsToAdd.length <= 0) {
            return;
        }

        this.clearRedoStack();
        this.undoRedoStack.push(stackItems);
        this.currentStep = this.undoRedoStack.length;
        this.updateUnsavedChangesFlag();
    }

    /**
     * Removes polygon from the historical records, so there is no way to restore it
     * @param {Polygon} polygonToForget
     */
    public removePolygonFromHistory(polygonToForget: Polygon): void {
        const newStack: StackItem[][] = [];

        // build new undo/redo stack which does not have a polygon
        this.undoRedoStack.forEach(stackItems => {
            const newStackItems: StackItem[] = [];
            stackItems.forEach(stackItem => {
                const loadedPolygons = this.createPolygons(stackItem.polygons);
                const filteredPolygons = loadedPolygons.filter(polygon => polygon.id !== polygonToForget.id);
                if (filteredPolygons.length > 0) {
                    newStackItems.push(
                        Object.assign({}, stackItem, { polygons: this.convertToExportedPolygons(filteredPolygons) })
                    );
                }
            });

            if (newStackItems.length > 0) {
                newStack.push(newStackItems);
            }
        });

        // after we have a new stack we need to adjust currentStep and savedOnStep flags
        const oldLength = this.undoRedoStack.length;
        const newLength = newStack.length;
        const diff = oldLength - newLength;

        this.currentStep -= diff;
        this.savedOnStep -= diff;
        this.undoRedoStack = newStack;
    }

    /*
     * Creates a stack item for undo/redo stack
     *
     * @param {StackAction} action Performed action: CREATE, UPDATE, DELETE
     * @param {Polygon[]} polygons List of polygons affected by the change
     * @param {boolean} willClosePolygon Set to true in case change you apply will close the polygons
     */
    private generateNewStackItem(action: StackAction, polygons: Polygon[], willClosePolygon = false): StackItem {
        const stackItem = {
            action,
            polygons: this.convertToExportedPolygons(polygons.map(polygon => polygon.clone())),
            saveable: polygons.some(polygon => !!polygon.neuralaClass.id && (polygon.closed || willClosePolygon))
        };

        return stackItem;
    }

    /**
     * Given a StackAction, determine what
     * the opposite action should be
     * So for example, if you created a polygon
     * and hit "Undo" the opposite action would
     * be to delete the polygon
     */
    private getInverseAction(action: StackAction): StackAction | null {
        switch (action) {
            case StackAction.CREATE:
                return StackAction.DELETE;
            case StackAction.UPDATE:
                return StackAction.UPDATE;
            case StackAction.DELETE:
                return StackAction.CREATE;
            case StackAction.SET:
                return StackAction.SET;
            default:
                return null;
        }
    }

    /**
     * Given a StackItem
     * executes the instructions contained
     * within that object
     *
     * Returns an action to "undo" the action that was just done
     */
    private performStackAction(item: StackItem): StackItem | null {
        const polygonsIndex = {};
        const polygonIDs: number[] = [];

        if (!item || !item.polygons) {
            return null;
        }
        const loadedPolygons = this.createPolygons(item.polygons);

        loadedPolygons.forEach((polygon, i) => {
            polygonsIndex[polygon.id] = i;
            polygonIDs.push(polygon.id);
        });

        const actionToPerform = this.getInverseAction(item.action);
        const polygonsToAlter = this.getAllChildren().filter(polygon => polygonIDs.includes(polygon.id));
        const project = this.getProject();
        if (!project) {
            return null;
        }

        let reverseState: StackItem | null = null;
        switch (actionToPerform) {
            case StackAction.CREATE:
                reverseState = this.generateNewStackItem(actionToPerform, [...loadedPolygons]);

                loadedPolygons.forEach(polygon => {
                    polygon.addTo(project);

                    if (polygon.fullySelected) {
                        this.addActivePolygon(polygon);
                    } else {
                        this.clearActivePolygon(polygon);
                    }
                });

                break;
            case StackAction.UPDATE:
                reverseState = this.generateNewStackItem(actionToPerform, polygonsToAlter);

                polygonsToAlter.forEach(polygon => {
                    const stackPolygon = loadedPolygons[polygonsIndex[polygon.id]];
                    const isActive = this.isPolygonActive(polygon);

                    if (reverseState && stackPolygon.neuralaClass.id !== null) {
                        reverseState.saveable = true;
                    }

                    this.removePolygon(polygon);
                    stackPolygon.addTo(project);

                    if (isActive) {
                        this.addActivePolygon(stackPolygon);
                    } else {
                        this.clearActivePolygon(stackPolygon);
                    }
                });

                break;
            case StackAction.DELETE:
                reverseState = this.generateNewStackItem(actionToPerform, polygonsToAlter);
                polygonsToAlter.forEach(polygon => this.removePolygon(polygon));
                break;
            case StackAction.SET:
                reverseState = this.generateNewStackItem(actionToPerform, this.getAllPolygons());

                this.getAllPolygons().forEach(polygon => {
                    this.removePolygon(polygon);
                });

                loadedPolygons.forEach(polygon => {
                    polygon.addTo(project);

                    if (polygon.fullySelected) {
                        this.addActivePolygon(polygon);
                    } else {
                        this.clearActivePolygon(polygon);
                    }
                });
                break;
            default:
                break;
        }

        return reverseState;
    }

    /********************
     *  Getter Methods  *
     *******************/

    public getScope(): paper.PaperScope {
        return this.scope;
    }

    public getProject(): NeuralaProject | null {
        return this.project;
    }

    public getAsset(): TaggableCanvasAsset | null {
        return this.asset;
    }

    public isAssetVisible(): boolean {
        return this.asset !== null && this.asset.visible;
    }

    public getCanvas(): HTMLCanvasElement | null {
        const view = this.getView();
        if (view) {
            return view.element;
        } else {
            return null;
        }
    }

    public getView(): paper.View | null {
        const project = this.getProject();

        if (project) {
            return project.view;
        }

        return null;
    }

    public getActiveAsset(): TaggableAsset | null {
        return this.activeImage;
    }

    /*************
     * Utilities *
     ************/

    public setScrollWheelStatus(status: boolean): void {
        this.scrollWheelEnabled = status;
    }

    public shiftIsDown(): boolean {
        return this.shiftPressed;
    }

    /* NOTE: This is a stopgap to allow workspace provider development.
     * Having this allows us to remove most keyboard event listeners and return to
     * the problem when the workspace provider is more mature.
     */
    public setShiftIsDown(state: boolean): void {
        this.shiftPressed = state;
    }

    /***********
     *  Image  *
     **********/

    public setAssetVisibility(status: boolean = true): void {
        const asset = this.getAsset();
        if (asset) {
            asset.visible = status;
        }
    }

    /**
     * If a point is near the boundary of an image, snap the point to the boundary
     * otherwise leave the point alone
     */
    public snapToImageIfNecessary(point: paper.Point): SnapPayload {
        const image = this.getAsset();
        const TOLERANCE = 7;

        const payload = {
            snap: false,
            point
        };

        if (!image) {
            return payload;
        }

        if (Math.abs(point.x - image.bounds.topLeft.x) <= TOLERANCE) {
            payload.snap = true;
            payload.point.x = image.bounds.topLeft.x;
        }

        if (Math.abs(point.x - image.bounds.bottomRight.x) <= TOLERANCE) {
            payload.snap = true;
            payload.point.x = image.bounds.bottomRight.x;
        }

        if (Math.abs(point.y - image.bounds.topLeft.y) <= TOLERANCE) {
            payload.snap = true;
            payload.point.y = image.bounds.topLeft.y;
        }

        if (Math.abs(point.y - image.bounds.bottomRight.y) <= TOLERANCE) {
            payload.snap = true;
            payload.point.y = image.bounds.bottomRight.y;
        }

        return payload;
    }

    /************************
     *  Polygon Management  *
     ***********************/

    public isPolygonActive(polygon: Polygon): boolean {
        return Number.isInteger(this.activePolygonsIndex[polygon.id]);
    }

    public updatePolygonClass(classObject: Class): void {
        return this.getAllChildren()
            .filter(item => item.neuralaType == 'Polygon' && item.closed && item.neuralaClass.id)
            .forEach((polygon: Polygon) => {
                if (polygon.neuralaClass.id == classObject.id) {
                    polygon.assignClass(classObject);
                    polygon.visible = !(classObject.visible === false);
                }
            });
    }

    public deletePolygonsForClass(classObject: Class): void {
        this.getAllChildren()
            .filter(item => item.neuralaType == 'Polygon' && item.closed && item.neuralaClass.id)
            .forEach((polygon: Polygon) => {
                if (polygon.neuralaClass.id == classObject.id) {
                    this.removePolygon(polygon);
                }
            });
    }

    public assignClass(polygon: Polygon, classObject: Class, isPrediction: boolean = false): void {
        if (isPrediction) {
            polygon.markClassAsPredicted();
        } else {
            polygon.markClassAsNotPredicted();
        }

        if (polygon.neuralaClass.id == classObject.id) {
            return;
        }

        const stackItem = this.createStackItem(StackAction.UPDATE, [polygon]);
        stackItem.saveable = polygon.closed ? true : false;
        this.addToUndoStack([stackItem]);

        polygon.assignClass(classObject);

        this.emitSaveableChange();
    }

    public createPolygon(layers: number[][][], fullySelected: boolean = true): Polygon | undefined {
        const project = this.getProject();
        if (!project) {
            return;
        }

        const polygon = new Polygon({
            children: layers.map(layer => new paper.Path(layer)),
            closed: true,
            fullySelected
        });
        polygon.addTo(project);

        return polygon;
    }

    public removePolygon(polygonToRemove: Polygon): void {
        const polygonLocation = this.activePolygonsIndex[polygonToRemove.id];

        if (Number.isInteger(polygonLocation)) {
            this.activePolygons.splice(polygonLocation, 1);
            this.calculateActivePolygonsIndex();
        }

        if (polygonToRemove.closed && polygonToRemove.neuralaClass.id) {
            this.emitSaveableChange();
        }

        polygonToRemove.remove();

        // temo solution
        // TODO: refactor this when history management will be moved out of canvas provider
        this.events.publish(EventName.CanvasPolygonRemoved);
    }

    private getAllChildren(): Polygon[] {
        const project = this.getProject();
        if (!project || !project.activeLayer) {
            return [];
        }

        return project.activeLayer.children as Polygon[];
    }

    public resetAllChildren(): void {
        const project = this.getProject();

        if (project && project.activeLayer) {
            project.activeLayer.children = [];
        }
    }

    /**
     * Fill in the polygon external ids if they aren't already set
     * This relies on the fact that brainbank will return the identifiers
     * in the order it gets the polygons.
     */
    public updatePolygonExternalIds(polygonExternalIds: NumberToStringMap): void {
        this.getPolygonsWithClasses().forEach(polygon => {
            if (!polygon.polygonIdentifier) {
                polygon.polygonIdentifier = polygonExternalIds[polygon.id];
            }
        });
    }

    public removeAllPolygons(): void {
        this.getAllChildren()
            .filter(item => item.neuralaType == 'Polygon')
            .slice(0)
            .forEach((polygon: Polygon) => this.removePolygon(polygon));
    }

    public export(filterFn: ((item: Polygon) => boolean) | null = null): ExportedPolygon[] {
        const defaultExportFilteringFunction = (item: Polygon) =>
            item.neuralaType == 'Polygon' && item.closed && item.neuralaClass.id;

        return this.getAllChildren()
            .filter(filterFn || defaultExportFilteringFunction)
            .map((polygon: Polygon) => {
                return Object.assign(polygon.export(), {
                    data: polygon.getPoints(this.normalizePoint.bind(this))
                });
            });
    }

    public exportForRedraw(): ExportedPolygon[] {
        const polygons = this.getAllPolygons();
        return this.convertToExportedPolygons(polygons);
    }

    public convertToExportedPolygons(polygons: Polygon[]): ExportedPolygon[] {
        return polygons.map((polygon: Polygon) => {
            return Object.assign(polygon.exportForRedraw(), {
                data: polygon.getPoints(this.normalizePoint.bind(this))
            });
        });
    }

    public getAllPolygons(): Polygon[] {
        return this.getAllChildren().filter(item => item.neuralaType == 'Polygon') || [];
    }

    public getClosedPolygons(): Polygon[] {
        return this.getAllChildren().filter(item => item.neuralaType == 'Polygon' && item.closed && item.visible) || [];
    }

    public getPolygonsWithClasses(): Polygon[] {
        return this.getAllChildren().filter(item => item.neuralaType == 'Polygon' && item.neuralaClass.id) || [];
    }

    public getPolygonsWithoutClasses(): Polygon[] {
        return this.getAllChildren().filter(item => item.neuralaType == 'Polygon' && !item.neuralaClass.id) || [];
    }

    public canSubtractPolygons(): boolean {
        return this.getClosedPolygons().length > 0;
    }

    private calculateActivePolygonsIndex(): void {
        this.activePolygonsIndex = {};

        this.getActivePolygons().forEach((polygon, i) => {
            this.activePolygonsIndex[polygon.id] = i;
        });
    }

    // Remove a polygon from the active stack
    public clearActivePolygon(polygon: Polygon): Polygon[] {
        const location = this.activePolygonsIndex[polygon.id];
        if (!Number.isInteger(location)) {
            return this.getActivePolygons();
        }

        if (this.activePolygons[location].isBoundingBox) {
            this.stopTrackingBoundingBoxResize(polygon.id);
        }

        this.activePolygons[location].deactivate();
        this.activePolygons.splice(location, 1);

        this.calculateActivePolygonsIndex();
        return this.getActivePolygons();
    }

    // Clears ALL polygons from the active stack
    public clearActivePolygons(): void {
        const activePolygons = this.getActivePolygons();

        activePolygons.forEach(polygon => {
            if (polygon.isBoundingBox) {
                this.stopTrackingBoundingBoxResize(polygon.id);
            }
            if (!this.isPolygonAScaleTool(polygon)) {
                polygon.deactivate();
            }
        });

        this.activePolygons = [];
        this.activePolygonsIndex = {};
    }

    // Clears and deleted ALL active polygons
    public removeActivePolygons(): void {
        const activePolygons = this.getActivePolygons();

        activePolygons.forEach(polygon => {
            polygon.remove();
        });

        this.clearRedoStack();
        this.activePolygons = [];
        this.activePolygonsIndex = {};

        if (activePolygons.length > 0) {
            this.emitSaveableChange();
        }

        // TODO: refactor this when history management will be moved out of canvas provider
        this.events.publish(EventName.CanvasPolygonRemoved);
    }

    // Add a polygon to the active stack
    public addActivePolygon(newPolygon: Polygon): Polygon[] {
        const location = this.activePolygonsIndex[newPolygon.id];

        if (!Number.isInteger(location)) {
            this.activePolygons.push(newPolygon);
            this.activePolygonsIndex[newPolygon.id] = this.activePolygons.length - 1;
        }

        newPolygon.activate();

        if (newPolygon.isBoundingBox) {
            this.startTrackingBoundingBoxResize(newPolygon.id);
        }

        return this.getActivePolygons();
    }

    public isPolygonAScaleTool(polygon: Polygon): boolean {
        return polygon && polygon.name != null;
    }

    public setDefaultClass(classObj: Class | null): void {
        this.defaultClass = classObj;
    }

    private validateListOfPolygons(polygons: Polygon[]): boolean {
        for (const polygon of polygons) {
            if (!(polygon instanceof Polygon)) {
                return false;
            }
        }
        return true;
    }

    // Set the active polygons, clearing out any old ones
    public setActivePolygons(polygons: Polygon[]): Polygon[] {
        if (!this.shiftPressed && this.validateListOfPolygons(polygons)) {
            this.clearActivePolygons();
        }

        polygons.forEach(polygon => {
            if (polygon instanceof Polygon) {
                if (this.shiftPressed) {
                    // shift-clicking a polygon toggles whether it is selected or not
                    if (this.activePolygonsIndex[polygon.id] === undefined) {
                        // the polygon isn't selected, so we select it
                        this.activePolygons.push(polygon);
                    } else {
                        // the polygon is selected and we need to deselect it
                        polygon.deactivate();
                        this.activePolygons.splice(this.activePolygonsIndex[polygon.id], 1);
                        delete this.activePolygonsIndex[polygon.id];
                    }
                } else {
                    this.activePolygons.push(polygon);
                }
            }
        });

        this.activePolygons.forEach((polygon, i) => {
            if (!this.isPolygonAScaleTool(polygon) && polygon instanceof Polygon) {
                if ((!polygon.closed || !polygon.neuralaClass || !polygon.neuralaClass.id) && this.defaultClass) {
                    this.assignClass(polygon, this.defaultClass);
                }
                polygon.activate();
                this.activePolygonsIndex[polygon.id] = i;

                if (polygon.isBoundingBox) {
                    this.startTrackingBoundingBoxResize(polygon.id);
                }
            }
        });

        return this.getActivePolygons();
    }

    public getActivePolygons(): Polygon[] {
        return this.activePolygons;
    }

    public getMutatingPolygons(): Polygon[] {
        return this.activePolygons.filter((polygon: Polygon) => polygon.isResizing);
    }

    public getClipboardPolygons(): Polygon[] {
        return this.polygonClipboard;
    }

    public setClipboardPolygons(polygons: Polygon[]): void {
        this.polygonClipboard = polygons;
    }

    public clearClipboardPolygons(): void {
        this.polygonClipboard = [];
    }

    /**
     * If drawing with a pen, background cut, or foreground cut, we need to determine
     * when it makes sense to autoclose the polygon instead of adding a new point
     */
    public shouldAutoClose(point: paper.Point, polygon: Polygon): boolean {
        const project = this.getProject();
        if (!project) {
            return false;
        }

        const AUTO_CLOSE_THRESHOLD = 20 / project.view.zoom;
        return point.getDistance(polygon.getOuterContour().getPointAt(0)) < AUTO_CLOSE_THRESHOLD;
    }

    /**
     * Run a method without triggering a change event
     */
    public runWithoutTriggeringChangeEvent(callback) {
        this.allowUnsavedChangesEvent = false;
        callback.bind(this)();
        this.allowUnsavedChangesEvent = true;
    }

    public clearPolygons(): void {
        this.runWithoutTriggeringChangeEvent(() => {
            this.removeAllPolygons();
        });
    }

    /**
     * Adjust the coordinates of the active polygons slightly (accounting for boundaries)
     * On views that support it (segmentation and object detection), this allows users
     * to move selected polygons a few pixels at a time by pressing arrow keys.
     * @param {string} direction The direction in which the polygon(s) should move
     * @param {number} amount The amount of pixels that the polygons(s) should move.
     */
    public nudgeSelectedPolygons(direction: string, amount: number) {
        const currentlySelectedPolygons = this.getActivePolygons();
        const newCoordinatesDelta = { x: 0, y: 0 };
        let canDoAllMoves = true;
        switch (direction) {
            case 'up':
                newCoordinatesDelta.y = -amount;
                break;
            case 'down':
                newCoordinatesDelta.y = amount;
                break;
            case 'left':
                newCoordinatesDelta.x = -amount;
                break;
            case 'right':
                newCoordinatesDelta.x = amount;
                break;
        }

        // Make sure users don't nudge the polygons out of bounds of image
        currentlySelectedPolygons.forEach(polygon => {
            const polygonNewBounds = new paper.Rectangle({
                x: polygon.bounds.x + newCoordinatesDelta.x,
                y: polygon.bounds.y + newCoordinatesDelta.y,
                width: polygon.bounds.width,
                height: polygon.bounds.height
            });

            if (!this.inBoundsRectangle(polygonNewBounds)) {
                canDoAllMoves = false;
            }
        });
        if (canDoAllMoves) {
            currentlySelectedPolygons.forEach(polygon => {
                polygon.position.x += newCoordinatesDelta.x;
                polygon.position.y += newCoordinatesDelta.y;
                if (polygon.isBoundingBox) {
                    polygon.updateBoundingBoxTools();
                }
                if (polygon.neuralaClass.id) {
                    this.emitSaveableChange();
                }

                const stackItem = this.createStackItem(StackAction.UPDATE, this.getActivePolygons());
                this.addToUndoStack([stackItem]);
            });
            this.detectCollisions();
        }
    }

    public pasteCurrentPolygons(currentWorkspaceType: WorkspaceType): void {
        const asset = this.getAsset();
        if (!asset) {
            return;
        }

        const canvasBounds = asset.bounds;

        // See BB-5972, Prevent pasting if the canvas is actively loading or changing and has no dimensions
        if (canvasBounds.left == 0 && canvasBounds.right == 0 && canvasBounds.top == 0 && canvasBounds.bottom == 0) {
            return;
        }

        this.clearActivePolygons();

        const undoStackItems: Polygon[] = [];
        let shouldEmitSaveableChangeEvent = false;
        const currentPolygons = this.getAllPolygons();
        let newPolygons = this.getClipboardPolygons();

        if (currentWorkspaceType === WorkspaceType.ObjectDetection) {
            const boundingBoxesOnly = newPolygons.filter(polygon => polygon.isBoundingBox);

            if (boundingBoxesOnly.length < newPolygons.length) {
                // If we have a mixture of polygons and bounding boxes,
                // filter out the polygons, and try to paste just the bounding boxes.
                newPolygons = [...boundingBoxesOnly]; // Spread to prevent referencing issues
                this.modalProvider.presentError(
                    this.translate.instant('Cannot Paste Copied Polygons'),
                    this.translate.instant(
                        'You cannot paste polygons onto Detection images. Please use the bounding box tool to classify objects within the image.'
                    ),
                    undefined,
                    this.translate.instant('OK')
                );

                if (newPolygons.length === 0) {
                    return;
                }
            }
        }

        const doesAllFit = newPolygons.every(
            ({ bounds }) =>
                Math.round(bounds.right) <= Math.round(canvasBounds.right) &&
                Math.round(bounds.left) >= Math.round(canvasBounds.left) &&
                Math.round(bounds.top) >= Math.round(canvasBounds.top) &&
                Math.round(bounds.bottom) <= Math.round(canvasBounds.bottom)
        );

        if (!doesAllFit) {
            this.modalProvider.presentError(
                this.translate.instant('Cannot Paste Copied Polygons'),
                this.translate.instant(
                    'Some of the copied polygons will not fit on the current image. Please make sure that the source and destination images have similar dimensions.'
                ),
                undefined,
                this.translate.instant('OK')
            );
            return;
        }

        newPolygons.forEach(value => {
            const newPolygon = value.clone();
            newPolygon.polygonIdentifier = undefined;
            newPolygon.isBoundingBox = value.isBoundingBox;
            if (newPolygon.hasNeuralaClass() && !shouldEmitSaveableChangeEvent) {
                shouldEmitSaveableChangeEvent = true;
            }

            // Figure out where to nudge the new polygon(s).
            // If the new location would be outside the image, wrap to the other side of the image.
            const newPosition = {
                x: newPolygon.position.x,
                y: newPolygon.position.y
            };

            const shouldNudge = currentPolygons.some(
                ({ position, bounds }) =>
                    position.x === newPolygon.position.x &&
                    position.y === newPolygon.position.y &&
                    bounds.width === newPolygon.bounds.width &&
                    bounds.height === bounds.height
            );

            if (shouldNudge) {
                newPosition.x =
                    newPolygon.bounds.right + 10 > canvasBounds.right
                        ? canvasBounds.left + newPolygon.bounds.width / 2
                        : newPosition.x + 10;
                newPosition.y =
                    newPolygon.bounds.bottom + 10 > canvasBounds.bottom
                        ? canvasBounds.top + newPolygon.bounds.height / 2
                        : newPosition.y + 10;

                newPolygon.position.x = newPosition.x;
                newPolygon.position.y = newPosition.y;
            }

            const project = this.getProject();
            if (!project) {
                return;
            }

            // We also need to generate a new ID property for the new polygon and make sure it doesn't clash with other IDs.
            newPolygon.setId(Math.max(0, ...this.getAllPolygons().map(value => value.id)) + 1);
            newPolygon.addTo(project);

            this.addActivePolygon(newPolygon);
            undoStackItems.push(newPolygon);

            // Defensive logic to ensure bounding boxes render properly
            if (newPolygon.isBoundingBox) {
                newPolygon.generateBoundingBoxManipulationTools();
                newPolygon.bbManipulationTools.forEach(tool => {
                    tool.obj.addTo(project);
                });
            }
        });
        const stackItem = this.createStackItem(StackAction.CREATE, undoStackItems);
        this.addToUndoStack([stackItem]);
    }

    public selectAllPolygons(): void {
        const project = this.getProject();
        if (!project) {
            return;
        }

        const allPolygons = this.getAllPolygons();
        allPolygons.forEach(polygon => {
            this.addActivePolygon(polygon);

            if (polygon.isBoundingBox) {
                // Defensive logic to ensure bounding boxes render properly
                polygon.generateBoundingBoxManipulationTools();
                polygon.bbManipulationTools.forEach(tool => {
                    tool.obj.addTo(project);
                });
            }
        });
    }

    public async deleteSelectedPolygons(): Promise<void> {
        const currentActivePolygons = this.getActivePolygons();

        // Request confirmation before deleting multiple polygons in one go
        if (currentActivePolygons.length > 1) {
            const shouldDelete = await this.muteableModalProvider.presentDeleteBulkPolygonsPrompt();
            if (shouldDelete) {
                this.continueDeletion(currentActivePolygons);
            }
        } else {
            this.continueDeletion(currentActivePolygons);
        }
    }

    private continueDeletion(currentActivePolygons: Polygon[]): void {
        if (currentActivePolygons.length > 0) {
            const stackItem = this.createStackItem(StackAction.DELETE, currentActivePolygons);
            this.addToUndoStack([stackItem]);
            this.removeActivePolygons();
            this.detectCollisions();
        }
    }

    /**
     * Store the currently selected polygons in our polygon clipboard, allowing users to later paste them elsewhere.
     * Any existing polygons in the clipboard are overwritten.
     */
    public copySelectedPolygons(): void {
        const currentActivePolygons = this.getActivePolygons();
        if (currentActivePolygons.length > 0) {
            this.setClipboardPolygons(currentActivePolygons.map(polygon => polygon.clone()));
        }
    }

    // In order to ensure users can undo bounding box resizes, we need to listen to events emitted
    // directly from the bounding box, starting when the user selects it.
    private startTrackingBoundingBoxResize(index: number): void {
        const activePolygons = this.getActivePolygons();
        if (activePolygons && activePolygons.length > 0) {
            const boxToTrack = activePolygons[this.activePolygonsIndex[index]];
            // Before tracking, we should make sure we haven't already subscribed
            // to the resize event (which could otherwise happen if a user clicks
            // a bounding box handle multiple times without resizing).
            const boxIsNotBeingTracked = !this.boundingBoxResizeSubscriptions.find(
                subscription => subscription.index === index
            );

            if (boxToTrack && boxIsNotBeingTracked) {
                // If all is well, create the subscription.
                const resizeSubscription = boxToTrack.boundingBoxResizeInProgress.subscribe(data => {
                    if (data === true) {
                        this.initialBoxState = this.createStackItem(StackAction.UPDATE, [boxToTrack]);
                    } else if (data === false) {
                        if (!this.initialBoxState) {
                            return;
                        }
                        this.addToUndoStack([this.initialBoxState]);
                        this.initialBoxState = null;
                    }
                });

                this.boundingBoxResizeSubscriptions.push({
                    index,
                    subscription: resizeSubscription
                });
            }
        }
    }

    // When a user deselects a bounding box, stop listening for resize events.
    private stopTrackingBoundingBoxResize(index: number): void {
        const boxToStopTracking = this.boundingBoxResizeSubscriptions.findIndex(
            subscription => subscription.index === index
        );

        if (boxToStopTracking >= 0) {
            this.boundingBoxResizeSubscriptions[boxToStopTracking].subscription.unsubscribe();
            this.boundingBoxResizeSubscriptions.splice(boxToStopTracking, 1);
        }
    }

    /*************
     *  Segment  *
     ************/

    public setActiveSegment(segment: paper.Segment | null): paper.Segment | null {
        this.activeSegment = segment;

        return this.getActiveSegment();
    }

    public getActiveSegment(): paper.Segment | null {
        return this.activeSegment;
    }

    public clearActiveSegment(): void {
        this.setActiveSegment(null);
    }

    public removeActiveSegment(): void {
        const activeSegment = this.getActiveSegment();

        if (activeSegment) {
            activeSegment.remove();
            this.clearActiveSegment();
        }
    }

    /*************************
     *  Viewport Management  *
     ************************/

    /**
     * Returns point which is inside asset
     * @param {paper.Point} point Project point
     */
    public adjustPointToAssetBounds(point: paper.Point): paper.Point | undefined {
        const project = this.getProject();
        if (!project) {
            return;
        }

        if (project.layers['assetLayer'].contains(point)) {
            return point;
        }

        const bounds = project.layers['assetLayer'].bounds;

        let newX = point.x;
        let newY = point.y;

        if (newX < bounds.left) {
            newX = bounds.left;
        } else if (newX > bounds.right) {
            newX = bounds.right;
        }

        if (newY < bounds.top) {
            newY = bounds.top;
        } else if (newY > bounds.bottom) {
            newY = bounds.bottom;
        }

        return new paper.Point(newX, newY);
    }

    /**
     * This method is used only in marquee tool.
     * TODO: revisit marquee tool (task has already been added to JIRA)
     */
    private adjustPointToBounds(x: number, y: number): paper.Point | undefined {
        const project = this.getProject();
        if (!project) {
            return;
        }

        const viewPoint = project.view.viewToProject(new paper.Point(x, y));
        const asset = this.getAsset();
        if (!asset) {
            return;
        }

        const bounds = asset.bounds;

        let newX = viewPoint.x;
        let newY = viewPoint.y;

        if (newX < bounds.left) {
            newX = bounds.left;
        } else if (newX > bounds.right) {
            newX = bounds.right;
        }

        if (newY < bounds.top) {
            newY = bounds.top;
        } else if (newY > bounds.bottom) {
            newY = bounds.bottom;
        }

        return new paper.Point(newX, newY);
    }

    public adjustPointToBoundsAndCanvas(x: number, y: number): paper.Point | undefined {
        const canvas = this.getCanvas();
        if (!canvas) {
            return;
        }

        const rect = canvas.getBoundingClientRect();
        let point = this.adjustPointToBounds(x - rect.left, y - rect.top);
        if (!point) {
            return;
        }

        if (this.grid && this.grid.visible) {
            point = this.grid.adjustPointToGrid(point);
        }

        return point;
    }

    // Ensure user cannot draw outside of the image bounds
    public inBounds(point: paper.Point): boolean {
        const asset = this.getAsset();
        if (!asset) {
            return false;
        }

        return (
            point.x >= asset.bounds.left &&
            point.x <= asset.bounds.right &&
            point.y >= asset.bounds.top &&
            point.y <= asset.bounds.bottom
        );
    }

    /**
     * Ensures a specified rectangle is
     * completely within bounds of the asset
     */
    public inBoundsRectangle(rect: paper.Rectangle): boolean {
        const asset = this.getAsset();
        if (!asset) {
            return false;
        }

        return asset.bounds.contains(rect);
    }

    /**
     * Tests the proposed new values of a bounding box to ensure it will be valid.
     * This function tests the proposed values without creating a new polygon for every test.
     *
     * @param {number} x Transformed x value
     * @param {number} y Transformed y value
     * @param {number} width New width
     * @param {number} height New height
     * @returns {boolean} valid bounding box
     * @memberof CanvasProvider
     */
    public willBeValidBoundingBox(x: number, y: number, width: number, height: number): boolean {
        this.tempRectangle.set(x, y, width, height);
        return this.inBoundsRectangle(this.tempRectangle);
    }

    public viewIsZoomedOut(zoom: number | null = null): boolean {
        if (!this.project) {
            return false;
        }

        const view = this.project.view;
        if (!view) {
            return false;
        }

        return view.zoom.toFixed(2) == this.initialZoom.toFixed(2);
    }

    public changeZoom(event: ChangeZoomEvent, scaleFactor: number = 1.075): boolean {
        const project = this.getProject();
        if (!project) {
            return false;
        }

        const viewPosition = project.view.viewToProject(new paper.Point(event.layerX, event.layerY));
        const oldZoom = project.view.zoom;
        const center = project.view.center;

        const newZoom = event.deltaY * -1 > 0 ? oldZoom * scaleFactor : oldZoom / scaleFactor;

        const zoomScale = oldZoom / newZoom;
        const pointerPositionMinusViewCenter = viewPosition.subtract(center);
        const centerTranslationVector = viewPosition
            .subtract(pointerPositionMinusViewCenter.multiply(zoomScale))
            .subtract(center);

        const didSetZoom = this.setZoom(newZoom);

        if (didSetZoom) {
            // If user zooms all the way out, let's recenter the image
            if (this.viewIsZoomedOut(project.view.zoom)) {
                this.setCenter(this.initialCenter);
            } else {
                this.setCenter(project.view.center.add(centerTranslationVector));
            }
        }

        this.updateBoundingBoxTools();

        event.stopPropagation();
        return false;
    }

    private updateBoundingBoxTools(): void {
        for (const polygon of this.activePolygons) {
            if (polygon.isBoundingBox) {
                polygon.updateBoundingBoxTools();
            }
        }
    }

    public setZoom(zoom: number): boolean {
        const project = this.getProject();
        const view = this.getView();
        if (!view || !project) {
            return false;
        }

        if (zoom < this.initialZoom) {
            zoom = this.initialZoom;
        } else if (zoom > 999) {
            zoom = 999;
        }

        if (zoom != project.view.zoom) {
            project.view.zoom = zoom;
            this.scope.settings.hitTolerance = 3 / view.zoom;
            this.events.publish(EventName.CanvasZoomChanged);
            return true;
        } else {
            return false;
        }
    }

    public changeCenter(event: WheelEvent): boolean {
        const project = this.getProject();
        if (!project) {
            return false;
        }

        const factor = 1 / project.view.zoom;
        const oldCenter = project.view.center;
        let offset = new paper.Point(event.deltaX, event.deltaY);

        offset = offset.multiply(factor);

        const newCenter = oldCenter.add(offset);

        this.setCenter(newCenter);

        event.preventDefault();
        return false;
    }

    // Ensures that a user does not pan out of bounds of the image
    private clampCenter(center: paper.Point): paper.Point | null {
        const asset = this.getAsset();
        const view = this.getView();
        if (!asset || !center || !view) {
            return null;
        }

        const viewSize = view.size;
        const imageBounds = asset.bounds;

        if (!imageBounds || !viewSize) {
            return null;
        }

        if (!this.initialCenter) {
            return null;
        }

        if (
            view.center.y - viewSize.height / 2 <= imageBounds.top &&
            view.center.y + viewSize.height / 2 >= imageBounds.bottom
        ) {
            center.y = this.initialCenter.y;
        } else {
            const topBound = center.y - viewSize.height / 2;
            if (topBound < imageBounds.top) {
                const diff = Math.abs(topBound - imageBounds.top);
                center.y += diff;
            }

            const bottomBound = center.y + viewSize.height / 2;
            if (bottomBound > imageBounds.bottom) {
                const diff = Math.abs(bottomBound - imageBounds.bottom);
                center.y -= diff;
            }
        }

        if (
            view.center.x - viewSize.width / 2 <= imageBounds.left &&
            view.center.x + viewSize.width / 2 >= imageBounds.right
        ) {
            center.x = this.initialCenter.x;
        } else {
            const leftBound = center.x - viewSize.width / 2;
            if (leftBound < imageBounds.left) {
                const diff = Math.abs(leftBound - imageBounds.left);
                center.x += diff;
            }

            const rightBound = center.x + viewSize.width / 2;
            if (rightBound > imageBounds.right) {
                const diff = Math.abs(rightBound - imageBounds.right);
                center.x -= diff;
            }
        }

        return center;
    }

    public setCenter(center: paper.Point | null): void {
        if (this.viewIsZoomedOut()) {
            center = this.initialCenter;
        }

        if (!center) {
            return;
        }

        center = this.clampCenter(center);

        if (!center) {
            return;
        }

        const newCenterX = center.x.toFixed(3);
        const newCenterY = center.y.toFixed(3);

        const project = this.getProject();
        if (!project) {
            return;
        }

        const currentCenterX = project.view.center.x.toFixed(3);
        const currentCenterY = project.view.center.y.toFixed(3);

        if (newCenterX != currentCenterX || newCenterY != currentCenterY) {
            project.view.center = center;
        }
    }

    /** When zoomed into an asset, move the user's view by the passed in amount of pixels.
     * This definition allows components to call a prebaked nudge function
     * without worrying about how it's implemented.
     * @param x How many pixels we should move the view horizontally.
     * @param y How many pixels we should move the view vertically.
     */
    public nudgeView(x: number, y: number): void {
        const view = this.getView();
        if (!view) {
            return;
        }

        this.setCenter(view.center.add(new paper.Point(x, y)));
    }

    /** When zoomed into an asset, pan the image so that it's centered within the canvas.
     * This is used to improve the behavior of the zoom input text fields.
     * We do lose any panning inside the image, though.
     */
    public resetViewToCenter() {
        this.setCenter(this.initialCenter);
    }

    /**
     * Find the x value needed to center a DOM element within the canvas area
     */
    public getCenteredPointOverCanvas(element: HTMLElement, xPercent = 0.5, yPercent = 0.5): number[] | undefined {
        const rect = element.getBoundingClientRect();
        const canvas = this.getCanvas();
        if (!canvas) {
            return;
        }

        const canvasRect = canvas.getBoundingClientRect();

        const x = canvasRect.width * xPercent - rect.width / 2;
        const y = canvasRect.height * yPercent - rect.height / 2;

        return [x, y];
    }

    public positionElementInCanvasWithPixels(element: HTMLElement, x: number, y: number): void {
        element.style.transform = `translate(${x}px, ${y}px)`;
    }

    /**
     * Adjust the element to keep it visible in canvas
     * If element is adjusted, return the new coordinates
     */
    public keepElementVisibleWithinCanvas(element: HTMLElement): number[] | undefined {
        const canvas = this.getCanvas();
        if (!canvas) {
            return;
        }

        const rect = element.getBoundingClientRect();
        const canvasRect = canvas.getBoundingClientRect();

        if (rect.width >= canvasRect.width) {
            return;
        }

        if (rect.height >= canvasRect.height) {
            return;
        }

        let x: number | null = null;
        let y: number | null = null;
        let adjust = false;
        if (rect.left < canvasRect.left) {
            x = 0;
            y = rect.top - canvasRect.top;
            adjust = true;
        } else if (rect.right > canvasRect.right) {
            x = canvasRect.right - canvasRect.left - rect.width;
            y = rect.top - canvasRect.top;
            adjust = true;
        } else if (rect.bottom > canvasRect.bottom) {
            x = rect.left - canvasRect.left;
            y = canvasRect.bottom - canvasRect.top - rect.height;
            adjust = true;
        }

        if (adjust && x && y) {
            this.positionElementInCanvasWithPixels(element, x, y);
            return [x, y];
        } else {
            return undefined;
        }
    }

    public applyGridSettings(state: null | GridState = null): void {
        if (this.grid && state) {
            this.grid.setColor(state.color);
            if (state.visible) {
                this.grid.show();
            } else {
                this.grid.hide();
            }
            this.grid.drawLayer(state.columns, state.rows);
        }
    }

    /***********
     *  Setup  *
     **********/

    public async setActiveAsset(image: TaggableAsset): Promise<void> {
        const project = this.getProject();

        try {
            this.ready = false;
            this.resetViewToCenter();
            this.clearUndoStack();
            this.setZoom(1);
            this.activeImage = image;

            if (project) {
                this.initialCenter = project.view.center.clone();
            } else {
                this.initialCenter = null;
            }

            await this.redraw();

            this.ready = true;
        } catch (err) {
            throw err;
        }
    }

    public loadImage(): Promise<void> {
        return new Promise(async (resolve, reject) => {
            const activeAsset = this.getActiveAsset();
            if (!activeAsset) {
                return;
            }

            if (!this.getProject()) {
                return;
            }

            /**
             * If crossOrigin is not specified during initialization, paperjs
             * throws a fit during resizing (cross-origin tainting error)
             */
            this.asset = new TaggableCanvasAsset({
                crossOrigin: 'anonymous',
                source: activeAsset.url
            });

            this.asset.addTo(this.assetLayer);
            this.asset.visible = false;
            this.asset.onLoad = () => {
                const project = this.getProject();
                if (project) {
                    this.asset.fitBounds(project.view.bounds, false);
                }

                this.asset.sourceRaster = this.asset.clone({ insert: false });
                this.asset.visible = true;

                return resolve();
            };

            this.asset.onError = e => {
                return reject(e);
            };
        });
    }

    public normalizePoint(point: paper.Point): paper.Point {
        const image = this.asset as TaggableCanvasAsset & ScalableImage;
        const relativePosition = point.subtract(image.bounds.point);

        // Round the point to 5 decimal places
        return new paper.Point(
            Math.round((100000 * relativePosition.x) / (image.width * image.getScaling().x)) / 100000,
            Math.round((100000 * relativePosition.y) / (image.height * image.getScaling().y)) / 100000
        );
    }

    public denormalizePoint(point: paper.Point): paper.Point | undefined {
        const asset = this.getAsset();
        if (!asset) {
            return;
        }

        const view = this.getView();
        if (!view) {
            return;
        }

        return new paper.Point(
            point.x * (asset.width * asset.scaling.x * view.zoom) + view.projectToView(asset.bounds.point).x,
            point.y * (asset.height * asset.scaling.y * view.zoom) + view.projectToView(asset.bounds.point).y
        );
    }

    public getAssetScale(): { x: number; y: number } | undefined {
        const asset = this.getAsset();
        if (!asset) {
            return;
        }

        return { x: asset.scaling.x, y: asset.scaling.y };
    }

    /**
     * Helper function for Region of Interest
     */
    public normalizePointToArray(point: paper.Point): number[] {
        const result = this.normalizePoint(point);
        return [result.x, result.y];
    }

    public denormalize(contour: number[][]): paper.Point[] | undefined {
        const denormalizedPoints: paper.Point[] = [];

        for (const point of contour) {
            /**
             * To denormalize a point, multiply it by the image dimensions * the zoom level
             * Additionally, since the image is centered we need to offset the points by how much the image has been shifted over/down;
             */
            const denormalizedPoint = this.denormalizePoint(new paper.Point(point[0], point[1]));
            if (!denormalizedPoint) {
                return;
            }

            const project = this.getProject();
            if (project) {
                denormalizedPoints.push(project.view.viewToProject(denormalizedPoint));
            }
        }

        return denormalizedPoints;
    }

    public normalize(contour: number[][]): paper.Point[] | undefined {
        const project = this.getProject();
        if (!project) {
            return;
        }

        return contour.map(point => {
            return project.view.viewToProject(this.normalizePoint(new paper.Point(point[0], point[1])));
        });
    }

    /**
     * Given a normalized width and height value on the canvas, return an absolute point on the reference frame of the original image
     * Some values are coming from a place where they are already normalized for the current zoom value.  If this is
     * the case, don't try to normalize them again
     */
    public getAbsoluteDimensions(
        width: number,
        height: number,
        alreadyZoomNormalized: boolean
    ): paper.Point | undefined {
        const asset = this.getAsset();
        const view = this.getView();
        if (!asset || !view) {
            return;
        }

        let xAbs = width / asset.scaling.x;
        let yAbs = height / asset.scaling.y;
        if (!alreadyZoomNormalized) {
            xAbs = xAbs / view.zoom;
            yAbs = yAbs / view.zoom;
        }

        return new paper.Point(Math.round(xAbs), Math.round(yAbs));
    }

    /**
     * Returns an "absolute" set of coordinates of a single point on an image, regardless of scaling or zoom factor.
     * The returned paper.Point is normalized, but the "origin" is the top-left of the image itself and not the canvas
     */
    public getAbsoluteCoordinatesFromViewPoint(
        x: number,
        y: number,
        alreadyZoomNormalized: boolean
    ): paper.Point | undefined {
        const asset = this.getAsset();
        const view = this.getView();
        if (!asset || !view) {
            return;
        }

        const viewPoint = new paper.Point(x, y);
        const assetPoint = view.projectToView(asset.bounds.point);

        return this.getAbsoluteDimensions(
            viewPoint.x - assetPoint.x,
            viewPoint.y - assetPoint.y,
            alreadyZoomNormalized
        );
    }

    public getAbsoluteCoordinates(x: number, y: number, alreadyZoomNormalized: boolean): paper.Point | undefined {
        const view = this.getView();
        if (!view) {
            return;
        }

        const viewPoint = view.projectToView(new paper.Point(x, y));
        return this.getAbsoluteCoordinatesFromViewPoint(viewPoint.x, viewPoint.y, alreadyZoomNormalized);
    }

    public getPolygonDimensionsText(polygon: Polygon): string {
        const absolutePoint = this.getAbsoluteDimensions(polygon.bounds.width, polygon.bounds.height, true);

        if (absolutePoint) {
            return this.getDimensionsText(absolutePoint.x, absolutePoint.y);
        } else {
            return '';
        }
    }

    public getROIDimensionsText(roi: RegionOfInterest): string {
        const absolutePoint = this.getAbsoluteDimensions(roi.getWidth(), roi.getHeight(), true);

        if (absolutePoint) {
            return this.getDimensionsText(absolutePoint.x, absolutePoint.y);
        } else {
            return '';
        }
    }

    public getDimensionsText(width: number, height: number): string {
        return `W: ${width}px, H: ${height}px`;
    }

    public getCoordinatesText(x: number, y: number): string {
        return `X: ${x}, Y: ${y}`;
    }

    /**
     * Determines whether there is room in some direction of the asset for an element of dimensions width x height
     * from position x/y without overflowing the asset.
     *
     * The incoming coordinates here should be relative to the canvas, which starts at 0,0 on the top-left
     *
     * @param x The x-coordinate of the point from which to check if there is room
     * @param y The y-coordinate of the point from which to check if there is room
     * @param roomWidth The width of the element that starts from x
     * @param roomHeight The height of the element that starts from y
     * @returns AssetRoom
     */
    public getAssetRoomFromCanvasContext(x: number, y: number, roomWidth: number, roomHeight: number): AssetRoom {
        const assetRect = this.getAssetBoundingClientRect();
        if (!assetRect) {
            return {
                left: false,
                right: false,
                top: false,
                bottom: false
            };
        }

        return {
            left: x - roomWidth >= assetRect.x,
            right: x + roomWidth <= assetRect.x + assetRect.width,
            top: y - assetRect.y >= roomHeight,
            bottom: y + roomHeight <= assetRect.y + assetRect.height
        };
    }

    /**
     * Determines whether there is room in some direction of the asset for an element of dimensions width x height
     * from position x/y without overflowing the asset.
     *
     * We convert the view-context coordinates to canvas-context coordinates, adjusting for zoom.  FYI the coordinates
     * are already adjusted for scaling.
     *
     * For example: the ROI-tool-provided coordinates are relative to the asset.
     *  However, if we are zoomed in and panned around, we need to still know where the actual edge of the canvas is.
     *
     * @param x The x-coordinate of the point from which to check if there is room
     * @param y The y-coordinate of the point from which to check if there is room
     * @param roomWidth The width of the element that starts from x
     * @param roomHeight The height of the element that starts from y
     * @returns AssetRoom
     */
    public getAssetRoomFromViewContext(x: number, y: number, roomWidth: number, roomHeight: number): AssetRoom {
        const view = this.getView();
        if (!view) {
            return {
                left: false,
                right: false,
                top: false,
                bottom: false
            };
        }

        const canvasX = (x - view.bounds.x) * view.zoom;
        const canvasY = (y - view.bounds.y) * view.zoom;

        return this.getAssetRoomFromCanvasContext(canvasX, canvasY, roomWidth, roomHeight);
    }

    private generateChildren(contours: number[][][] | null) {
        const children: paper.Path[] = [];

        if (!!contours) {
            contours.forEach(contour => {
                const denormalizedPoints = this.denormalize(contour);
                if (!denormalizedPoints) {
                    return;
                }

                children.push(new paper.Path({ segments: denormalizedPoints, closed: true }));
            });
        }

        return children;
    }

    /**
     * Get Polygons list
     */
    private getPolygons(rawPolygons: ExportedPolygon[] | null = null): Polygon[] {
        const polygons = rawPolygons || this.getActiveAssetPolygons();

        if (!polygons) {
            return [];
        }

        return this.createPolygons(polygons);
    }

    private createPolygons(polygons: ExportedPolygon[]): Polygon[] {
        return polygons.map(polygon => {
            // Create a new polygon object and assign a class to it
            const classObject = this.classProvider.getClassByID(polygon.classIdentifier);

            const newPolygon = new Polygon({
                children: this.generateChildren(polygon.data),
                fullySelected: false,
                polygonIdentifier: polygon.polygonIdentifier,
                insert: false,
                neuralaClass: polygon.neuralaClass ? polygon.neuralaClass : classObject,
                visible: !classObject || classObject.visible !== false,
                defaultNoClassColor: this.noClassColor,
                prediction: polygon.prediction || null
            });

            if (polygon.predictionFlag) {
                newPolygon.markClassAsPredicted();
            }

            if (polygon.clientIdentifier) {
                newPolygon.setId(polygon.clientIdentifier);
            }

            return newPolygon;
        });
    }

    private getActiveAssetPolygons() {
        const activeAsset = this.getActiveAsset();

        if (activeAsset && activeAsset.polygons) {
            return activeAsset.polygons;
        }

        return [];
    }

    /**
     * Load Polygons list and add to current project
     */
    public loadPolygons(polygons: ExportedPolygon[]): Polygon[] {
        const loadedPolygons = this.getPolygons(polygons);

        if (this.project) {
            for (const polygon of loadedPolygons) {
                polygon.addTo(this.project);
            }
        }

        return loadedPolygons;
    }

    public reset(): void {
        if (this.project) {
            this.project.clear();
        }
        this.originalROIPoints = null;

        this.roi = null;
        this.grid = null;
        this.clearUndoStack();
        this.activeImage = null;
        this.clearActiveSegment();
        this.clearActivePolygons();
        this._hasUnsavedChanges = false;
    }

    public init(canvas: HTMLCanvasElement, workspaceType: WorkspaceType): void {
        if (canvas) {
            this.canvas = canvas;
        }

        if (this.canvas) {
            this.canvas.removeEventListener('wheel', this.wheelHandler, true);
            this.canvas.addEventListener('wheel', this.wheelHandler, true);
            this.canvas.oncontextmenu = (e: MouseEvent) => {
                const view = this.getView();

                if (view) {
                    const point = view.viewToProject(new paper.Point(e.offsetX, e.offsetY));
                    return !this.inBounds(point);
                } else {
                    return false;
                }
            };
        }

        this.scope = new paper.PaperScope();
        this.scope.settings.handleSize = 7;
        this.scope.settings.hitTolerance = 3;

        this.project = new NeuralaProject(this.canvas);
        this.project.events = this.events;
        this.project.setCursor = this.setCursor.bind(this);

        // Order of layers is important
        // polygons must be on top of all other layers
        this.assetLayer = new paper.Layer({ name: 'assetLayer' });
        this.grid = new GridLayer({ name: 'gridLayer', active: false, selected: false, locked: true });
        this.polygonsLayer = new paper.Layer({
            name: 'polygons',
            active: true,
            selected: true
        });

        this.polygonsLayer.addTo(this.project);
        this.grid.addTo(this.project);

        const extendedView = (this.project.view as object) as ExtendedView;

        // Extend the paperjs update method to link with angular change detection
        this.project.view.update = () => {
            if (!this.project) {
                return false;
            }

            if (!extendedView._needsUpdate) {
                return false;
            }

            const extendedProject = (this.project as object) as ExtendedProject;
            const viewContext = extendedView._context;
            const size = this.project.view.viewSize;

            viewContext.clearRect(0, 0, size.width + 1, size.height + 1);

            if (extendedProject) {
                this.zone.run(() => {
                    if (this.project) {
                        extendedProject.draw(viewContext, this.project.view.matrix, this.project.view.pixelRatio);
                    }
                });
            }

            extendedProject._needsUpdate = false;

            return true;
        };
    }

    /********************
     *  Event Handlers  *
     *******************/
    private handleWheelEvent(event: WheelEvent): void {
        if (!this.ready || !this.scrollWheelEnabled || this.locked) {
            return;
        }

        if (!event.shiftKey) {
            if (Math.abs(event.deltaY) > 1) {
                this.changeZoom((event as object) as ChangeZoomEvent);
            }
        } else if (event.shiftKey) {
            this.changeCenter(event);
        }
    }

    // Need to re-activate active polygons/segments
    // Need to regenerate undo/redo stack
    public async redraw(): Promise<void> {
        const polygons = this.exportForRedraw();
        const normalizedClipboardPolygons = this.convertToExportedPolygons(this.polygonClipboard);
        const hasROI = this.roi ? true : false;
        const roiPoints = this.exportROICooordinates();
        const roiWasChanged = this.roi && this.roi.hasChanges();
        const canvas = this.getCanvas();

        let gridState: GridState | null = null;
        if (this.grid) {
            gridState = {
                columns: this.grid.getColumns(),
                rows: this.grid.getRows(),
                visible: this.grid.visible,
                color: this.grid.getColor()
            };
        }

        /**
         * See BB-5956, Save these cached values so we can correctly redraw at the required zoom/center
         * Without this, the image will reset to a non-zoomed state during window resizes
         */
        let cachedZoom: number | undefined;
        let cachedCenter;
        if (this.project) {
            cachedZoom = this.project.view.zoom;
            cachedCenter = this.project.view.center.clone();
        }

        try {
            this.ready = false;

            if (this.initialCenter) {
                this.setCenter(this.initialCenter);
            }

            this.setZoom(this.initialZoom);

            const activePolygonsIndex = this.activePolygonsIndex;

            if (this.polygonsLayer) {
                this.polygonsLayer.removeChildren();
            }

            if (this.assetLayer) {
                this.assetLayer.removeChildren();
            }

            if (this.roi && hasROI) {
                this.roi.remove();
            }

            if (canvas && this.project) {
                const { width: newWidth, height: newHeight } = canvas.getBoundingClientRect();
                this.project.view.viewSize = new paper.Size(newWidth, newHeight);
            }

            this.clearActiveSegment();
            this.clearActivePolygons();

            await this.loadImage();

            this.applyGridSettings(gridState);

            this.zone.runOutsideAngular(async () => {
                this.loadPolygons(polygons);
                this.setClipboardPolygons(this.getPolygons(normalizedClipboardPolygons));
                this.detectCollisions();

                const polygonMap = {};
                this.getAllPolygons().forEach(p => (polygonMap[p.id] = p));

                for (const polygonId in activePolygonsIndex) {
                    if (polygonMap[polygonId]) {
                        this.addActivePolygon(polygonMap[polygonId]);
                    }
                }

                if (this.roi && hasROI) {
                    this.drawROI(roiPoints);

                    if (!roiWasChanged) {
                        this.roi.markAsSaved();
                    }
                }
            });

            if (this.project) {
                this.initialCenter = this.project.view.center;
            }

            this.ready = true;
        } catch (err) {
            throw err;
        }

        if (cachedZoom !== undefined && cachedCenter !== undefined) {
            this.setZoom(cachedZoom);
            this.setCenter(cachedCenter);
            this.updateBoundingBoxTools();
        }
    }

    /*************************
     *  Collision Detection  *
     ************************/

    /**
     * Remove all collisions currently on the screen
     */
    private removeAllCollisions(): void {
        this.getAllChildren()
            .slice(0)
            .forEach((polygon: Polygon) => {
                if (polygon.neuralaType == 'Collision') {
                    polygon.remove();
                }
            });

        this.hasOverlaps = false;
    }

    /**
     * Checks whether or not two bounding boxes intersect
     */
    public doBoundingBoxesIntersect(a: paper.Rectangle, b: paper.Rectangle): boolean {
        return (
            a.topLeft.x <= b.bottomRight.x &&
            a.bottomRight.x >= b.topLeft.x &&
            a.topLeft.y <= b.bottomRight.y &&
            a.bottomRight.y >= b.topLeft.y
        );
    }

    /**
     * Check to see if we should detect collisions on two Polygons
     * We perform a number of checks to weed out any instances
     * where we know checking for intersections are not necessary
     * 1) Check if polygonA is polygonB, if so, return false
     * 2) Check if polygonA or polygonB are both of type "Polygon", if not return false
     * 3) Check if we have already calculated polygonA with polygonB, if so return false
     * (because we don't then need to do polygonB with polygonA)
     * 4) Check if the bounding boxs of polygonA and polygonB intersect. If so, there is a chance
     * that the polygons will intersect, though not guaranteed. If the bounding boxes do not intersect
     * there is no chance that the polygons will intersect, so we can safely ignore this combo.
     * 5) if polygons A and B are boundingboxes, that means that it is a detection workspace, and thus, dont check for collisions
     *
     * Ideas for Further Optimizations:
     * 1. Can we check to see if a polygon has been changed at all? If two polygons have not been
     * changed, maybe we can just cache their collision and not bother with computing intersection again.
     * 2. One day, it would be nice to give the martinez library a try again: https://github.com/w8r/martinez.
     * It was VERY fast, but it had enough bugs that we couldn't realistically rely on it in a production scenario,
     * but if they iron out enough of the bugs it would be great to use it again because performance could definitely improve.
     */
    private shouldDetectCollisions(
        polygonA: Polygon,
        polygonB: Polygon,
        collisionsAlreadyComputed: CollisionsMap = {}
    ): boolean {
        const AB = polygonA.id.toString() + polygonB.id.toString();
        const BA = polygonB.id.toString() + polygonA.id.toString();

        if (
            !polygonA.closed ||
            !polygonB.closed ||
            polygonA.id == polygonB.id ||
            polygonA.neuralaType != 'Polygon' ||
            polygonB.neuralaType != 'Polygon' ||
            collisionsAlreadyComputed[AB] ||
            collisionsAlreadyComputed[BA] ||
            !this.doBoundingBoxesIntersect(polygonA.bounds, polygonB.bounds) ||
            polygonA.isBoundingBox ||
            polygonB.isBoundingBox
        ) {
            return false;
        }

        collisionsAlreadyComputed[AB] = true;
        collisionsAlreadyComputed[BA] = true;

        return true;
    }

    public getPolygonIntersection(polygonA: Polygon, polygonB: Polygon) {
        const clipperA = this.neuralaToClipper(polygonA);
        const clipperB = this.neuralaToClipper(polygonB);
        return this.getClipperIntersection(clipperA, clipperB);
    }

    public getRectangleIntersection(polygon: Polygon, area: paper.Rectangle) {
        const clipperA = this.neuralaToClipper(polygon);
        const clipperB = this.rectangleToClipper(area);
        return this.getClipperIntersection(clipperA, clipperB);
    }

    private getClipperIntersection(clipperA, clipperB) {
        const clipper = new ClipperLib.Clipper();
        const polytree = new ClipperLib.PolyTree();

        clipper.AddPaths(clipperA, ClipperLib.PolyType.ptSubject, true);
        clipper.AddPaths(clipperB, ClipperLib.PolyType.ptClip, true);
        clipper.Execute(
            ClipperLib.ClipType.ctIntersection,
            polytree,
            ClipperLib.PolyFillType.pftNonZero,
            ClipperLib.PolyFillType.pftNonZero
        );

        /**
         * Since ClipperLib uses ints internally, we need to scale our points
         * up and down to preserve the floating point values
         * As a result, there are sometimes floating point errors in calculating
         * collisions. This value is used to account for those and filter them out
         */
        return ClipperLib.JS.PolyTreeToExPolygons(polytree)
            .filter(polygon => {
                return ClipperLib.JS.AreaOfPolygon(polygon.outer) > this.FLOATING_POINT_ERROR_THRESHOLD;
            })
            .map(polygon => {
                return this.exPolygonToNeurala(polygon);
            });
    }

    public getPolygonUnion(polygonA: Polygon, polygonB: Polygon) {
        const clipperA = this.neuralaToClipper(polygonA);
        const clipperB = this.neuralaToClipper(polygonB);
        return this.getClipperUnion(clipperA, clipperB);
    }

    public getRectangleUnion(polygon: Polygon, area: paper.Rectangle) {
        const clipperA = this.neuralaToClipper(polygon);
        const clipperB = this.rectangleToClipper(area);
        return this.getClipperUnion(clipperA, clipperB);
    }

    private getClipperUnion(clipperA: ClipperPoint[][], clipperB: ClipperPoint[][]) {
        const clipper = new ClipperLib.Clipper();
        const polytree = new ClipperLib.PolyTree();

        clipper.AddPaths(clipperA, ClipperLib.PolyType.ptSubject, true);
        clipper.AddPaths(clipperB, ClipperLib.PolyType.ptClip, true);
        clipper.Execute(
            ClipperLib.ClipType.ctUnion,
            polytree,
            ClipperLib.PolyFillType.pftNonZero,
            ClipperLib.PolyFillType.pftNonZero
        );

        /**
         * Since ClipperLib uses ints internally, we need to scale our points
         * up and down to preserve the floating point values
         * As a result, there are sometimes floating point errors in calculating
         * collisions. This value is used to account for those and filter them out
         */
        return ClipperLib.JS.PolyTreeToExPolygons(polytree)
            .filter(polygon => {
                return ClipperLib.JS.AreaOfPolygon(polygon.outer) > this.FLOATING_POINT_ERROR_THRESHOLD;
            })
            .map(polygon => {
                return this.exPolygonToNeurala(polygon);
            });
    }

    /**
     * Detect any overlapping polygons and render them on the screen
     */
    public detectCollisions(): void {
        this.removeAllCollisions();

        const currentPolygons = this.getAllChildren().slice(0);
        const collisionsAlreadyComputed = {};

        currentPolygons.forEach((polygonA: Polygon) => {
            currentPolygons.forEach((polygonB: Polygon) => {
                if (!this.shouldDetectCollisions(polygonA, polygonB, collisionsAlreadyComputed)) {
                    return;
                }

                const polygons = this.getPolygonIntersection(polygonA, polygonB);

                if (polygons.length > 0) {
                    this.hasOverlaps = true;
                }

                polygons.forEach(polygon => {
                    // tslint:disable-next-line: no-unused-expression
                    new Collision({
                        children: polygon.map(layer => new paper.Path({ segments: layer, closed: true }))
                    });
                });
            });
        });

        this.events.publish(EventName.CanvasOverlappingPolygons, this.hasOverlaps);
    }

    /**************************
     *  ClipperLib Utilities  *
     *************************/

    public neuralaToClipper(polygon: Polygon): ClipperPoint[][] {
        return polygon.children.map(item => {
            const paperPath = item as paper.Path;
            const toObject = paperPath.segments.map(segment => {
                return {
                    X: segment.point.x * 1000000,
                    Y: segment.point.y * 1000000
                };
            });

            toObject.push(toObject[0]);

            return toObject;
        });
    }

    public rectangleToClipper(area: paper.Rectangle): ClipperPoint[][] {
        const clipperResult: ClipperPoint[] = [];
        clipperResult.push({
            X: area.topLeft.x * 1000000,
            Y: area.topLeft.y * 1000000
        });
        clipperResult.push({
            X: area.topRight.x * 1000000,
            Y: area.topRight.y * 1000000
        });
        clipperResult.push({
            X: area.bottomRight.x * 1000000,
            Y: area.bottomRight.y * 1000000
        });
        clipperResult.push({
            X: area.bottomLeft.x * 1000000,
            Y: area.bottomLeft.y * 1000000
        });
        // This is not a typo, we need the clipper array to start and end with the same point to complete the polygon
        clipperResult.push({
            X: area.topLeft.x * 1000000,
            Y: area.topLeft.y * 1000000
        });
        return [clipperResult];
    }

    public exPolygonToNeurala(exPolygon: ClipperExPolygon) {
        const contour = exPolygon.outer.map(point => new paper.Point(point.X / 1000000, point.Y / 1000000));
        const layers = exPolygon.holes.map(hole => {
            return hole.map(point => new paper.Point(point.X / 1000000, point.Y / 1000000));
        });

        layers.unshift(contour);

        return layers;
    }

    /**
     * Applies settings to the canvas and redraws it
     */
    public applyWorkspaceSettings(settings: CanvasSettings): void {
        const newSettings = Object.assign(
            {
                labelsEnabled: this.labelsEnabled,
                noClassColor: this.noClassColor,
                predictionsEnabled: this.predictionsEnabled
            },
            settings
        );
        this.labelsEnabled = newSettings.labelsEnabled;
        this.noClassColor = newSettings.noClassColor;
        this.predictionsEnabled = newSettings.predictionsEnabled;

        this.getAllPolygons().forEach((polygon: Polygon) => {
            polygon.setNoClassColor(this.noClassColor);
            if (polygon.neuralaType == 'Polygon' || polygon.neuralaType == 'Collision') {
                polygon.visible = true;
                polygon.fullySelected = false;
            }
        });
    }

    /******************************
     *  Canvas Filter Management  *
     *****************************/

    /**
     * Update the filter string on the canvas object with the given values
     *
     * @return void
     */
    public updateFilter(options): void {
        const { asset } = this;
        const currentFilterString = this.getCanvasContextFilter(asset);

        // split the current filter using the \w expression to map properties to values
        // WARNING: WILL NOT WORK WITH THE drop-shadow AND hue-rotate PROPERTIES
        const properties = currentFilterString.split(/[()\ ]/);

        // tslint:disable-next-line: prettier
        const oldBrightness = properties.indexOf('brightness') < 0 ? 1 : properties[properties.indexOf('brightness') + 1];
        const oldContrast = properties.indexOf('contrast') < 0 ? 1 : properties[properties.indexOf('contrast') + 1];
        asset.drawImage(asset.sourceRaster.canvas, new paper.Point(0, 0));

        // tslint:disable-next-line: prettier
        const filterString = `brightness(${options.brightness || oldBrightness}) contrast(${options.contrast || oldContrast})`;

        this.setCanvasContextFilter(asset, filterString);

        asset.drawImage(asset.canvas, new paper.Point(0, 0));
    }

    /**
     * Reset the current canvas filter to its original state. Sometimes the canvas will be an update behind, so we run this logic twice
     */
    public resetFilter(): void {
        const { asset } = this;

        asset.drawImage(asset.sourceRaster.canvas, new paper.Point(0, 0));
        this.setCanvasContextFilter(asset, 'brightness(100%) contrast(100%)');

        asset.drawImage(asset.canvas, new paper.Point(0, 0));
        asset.drawImage(asset.sourceRaster.canvas, new paper.Point(0, 0));
        this.setCanvasContextFilter(asset, 'brightness(100%) contrast(100%)');

        asset.drawImage(asset.canvas, new paper.Point(0, 0));
    }

    /**
     * Check if the unknown item is a bounding box, while first confirming
     * that it is a polygon.
     *
     * @param item The item we are checking against
     */
    public safeIsBoundingBoxCheck(item: paper.Item): boolean {
        if (item instanceof Polygon) {
            return (item as Polygon).isBoundingBox;
        }

        return false;
    }

    /**
     * Provide a single place to get the current showPixelDimensions setting with a sane default
     */
    public shouldShowPixelDimensions(): boolean {
        return this.settingsProvider.get('detection.workspace.showPixelDimensions') === true;
    }

    /**
     * Returns a Rectangle which describes bounding box and a location of an asset on canvas.
     * The result is in "real" pixels.
     *
     * One of use-cases: marquee tool.
     * It is useful to check that the mouse is inside asset.
     * And it helps to calculate the size and location of guidelines.
     * The problem here is that we must use "real" pixels to define location and a size of a guideline
     * and we need to make sure that the mouse is moved over the assets. Our asset is drawn on canvas,
     * so it doesn't have normal boundaries as any other tag has and we must calculate them by ourself.
     */
    public getAssetBoundingClientRect(): AssetBoundingClientRect | undefined {
        const canvas = this.getCanvas();
        const asset = this.getAsset();
        const project = this.getProject();
        if (!asset || !canvas || !project) {
            return;
        }

        const zoom = project.view.zoom;

        // To compute any dimension of an asset we see on the screen
        // we must use zoom and scaling of an assset
        const visibleWidth = asset.width * zoom * asset.scaling.x;
        const visibleHeight = asset.height * zoom * asset.scaling.y;

        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        let x = 0;
        let y = 0;
        let width = 0;
        let height = 0;
        let top = 0;
        let right = 0;
        let bottom = 0;
        let left = 0;

        // if zoomed in asset is wider then canvas
        // then our bounding rectangle's width is the same as our canvas
        if (visibleWidth > canvasWidth) {
            right = canvasWidth;
            x = 0;
            left = 0;
            width = canvasWidth;
        } else {
            // As for now we always place asset in the middle of the canvas
            x = (canvasWidth - visibleWidth) / 2;
            left = x;
            right = x + visibleWidth;
            width = visibleWidth;
        }

        // if zoomed in asset is taller then canvas
        // then our bounding rectangle's height is the same as our canvas
        if (visibleHeight > canvasHeight) {
            y = 0;
            top = 0;
            bottom = canvasHeight;
            height = canvasHeight;
        } else {
            // As for now we always place asset in the middle of the canvas
            y = (canvasHeight - visibleHeight) / 2;
            top = 0;
            bottom = y + visibleHeight;
            height = visibleHeight;
        }

        return {
            x,
            y,
            top,
            right,
            bottom,
            left,
            width,
            height
        };
    }

    private getCanvasContextFilter(asset: TaggableCanvasAsset): string {
        const canvasContext = this.getCanvasContext(asset);
        const canvasFilters = (canvasContext as object) as ExtendedCanvasFilters;
        return canvasContext ? canvasFilters.filter || '' : '';
    }

    private setCanvasContextFilter(asset: TaggableCanvasAsset, filter: string) {
        const canvasContext = this.getCanvasContext(asset);
        if (canvasContext) {
            const canvasFilters = (canvasContext as object) as ExtendedCanvasFilters;
            canvasFilters.filter = filter;
        }
    }

    private getCanvasContext(asset: TaggableCanvasAsset) {
        return asset.canvas.getContext('2d');
    }
}



// WEBPACK FOOTER //
// ./src/providers/workspace/canvas.ts